



































































import {
  Component,
  namespace,
  State,
  Vue,
  Watch
} from 'nuxt-property-decorator';
import Highlighter from 'web-highlighter/dist';
import ToastService, { ToastType } from '../../toast/ToastService';
import CloseBtn from '../CloseBtn.vue';
import { NotesClickedFrom, NotesMode } from './NotesMode';
import { NoteMeta } from '@/types/noteMeta';
import { NoteHighlight, NoteState } from '@/types/highlightContract';
import { Site } from '@/types/initialData';
import { Page } from '@/types/pageContract';
import NotesStore from '@/store/notes.store';
import SidePanelStore from '@/components/ui/side-panel/side-panel.store';
import KeyHandleEventBus from '@/store/keyHandle.eventbus';

const MarkdownEditor = () => import('./MarkdownEditor.vue');
const webHighlighterStore = namespace('webHighlighter.store');
const highlightsModule = namespace('highlights.store');

@Component({
  components: { CloseBtn, MarkdownEditor }
})
export default class CreateNote extends Vue {
  comment: string | null = null;
  isSaving: boolean = false;
  availableColors: string[] = [
    'hl-default',
    'hl-green',
    'hl-red',
    'hl-blue',
    'hl-yellow',
    'hl-purple'
  ];

  selectedColor: string = 'hl-default';
  showClosePrompt: boolean = false;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site)
  site: Site;

  // @ts-ignore
  @State((state) => state.typo3?.page?.page)
  page!: Page;

  // @ts-ignore
  @State((state) => state.typo3?.page?.page?.id) // notice : there's also id, and that reflects the query param
  pageId: string;

  @webHighlighterStore.Getter
  currentId: string | null;

  @webHighlighterStore.Mutation
  setCurrentId: (id: string | null) => void;

  @webHighlighterStore.Getter
  highlighter: Highlighter;

  @webHighlighterStore.Getter
  idMap: Map<String, NoteMeta>;

  @webHighlighterStore.Getter
  noteMap: Map<String, String>;

  @highlightsModule.Action
  updateHighlightFromPage: ({
    pid: string,
    site: Site,
    highlight: NoteHighlight,
    page: Page
  }) => Promise<NoteHighlight>;

  @highlightsModule.Action
  addHighlightToPage: ({
    pid: string,
    site: Site,
    highlight: NoteHighlight,
    page: Page
  }) => Promise<NoteHighlight>;

  get isEditing() {
    return this.mode === NotesMode.EDIT;
  }

  get updatedEdit(): boolean {
    return NotesStore.updatedEdit;
  }

  get mode(): NotesMode {
    return NotesStore.mode;
  }

  get sidePanelOpen() {
    return SidePanelStore.rightPanelIsOpen;
  }

  get currentNote() {
    return this.idMap.get(this.currentId);
  }

  private static closeSidePanelIfClickedFromPreview(): void {
    if (NotesStore.notesClickedFrom === NotesClickedFrom.PREVIEW) {
      // We want to close the side panel after creating a new note
      SidePanelStore.rightPanelIsOpen = false;
    }
  }

  // Update data if new edit
  @Watch('updatedEdit')
  onIsEditingChange(newEdit: boolean): void {
    if (newEdit) {
      const updatedNote = this.idMap.get(this.currentId);
      this.comment = updatedNote.highlight.userComment;
      this.selectedColor = updatedNote.highlight.hlcolor;
    }
  }

  @Watch('sidePanelOpen')
  sidePanelOpenChanged(isOpen: boolean) {
    if (!isOpen && !this.isSaving) {
      this.cancelHighlight();
    }
  }

  mounted() {
    if (this.isEditing) {
      this.comment = this.currentNote?.highlight?.userComment;
      this.selectedColor = this.currentNote?.highlight?.hlcolor;
    } else {
      // console.log(
      //   "Let's create a new note with webhighlighter id:",
      //   this.currentId
      // );
      this.resetFields();
    }
    KeyHandleEventBus.$on('escape', this.handleClose);
  }

  destroyed() {
    KeyHandleEventBus.$off('escape', this.handleClose);
  }

  saveClicked() {
    this.isSaving = true;
    this.saveHighlight();
  }

  async saveHighlight() {
    const current = this.idMap.get(this.currentId);
    if (!current) {
      this.isSaving = false;
      this.cancelHighlight();
      return;
    }
    let error = false;
    let updatedHighlight;
    if (this.isEditing) {
      const highlightedDom = this.highlighter.getDoms(this.currentId)[0];
      // Set fields here
      const range = document.createRange();
      // return;
      if (highlightedDom) {
        // If we have the highlightedDom we get the ranges
        // and use if highlight is pageNote
        range.selectNode(highlightedDom as HTMLElement);
      }

      // We choose to explicitly handle page notes, to prevent
      // interrupting current functionality
      if (current.highlight.recreationData?.ibkngData?.isPageNote) {
        updatedHighlight = {
          ...current.highlight,
          ranges: [range],
          hlcolor: this.selectedColor,
          userComment: this.comment
        };
      } else {
        updatedHighlight = {
          ...current.highlight,
          hlcolor: this.selectedColor,
          userComment: this.comment
        };
      }

      try {
        const { highlight } = await this.updateHighlightFromPage({
          pid: updatedHighlight.pid || this.pageId,
          site: this.site,
          highlight: updatedHighlight,
          page: this.page
        });
        this.idMap.set(this.currentId, {
          ...current,
          highlight,
          noteState: NoteState.REMOTE
        });
        // console.log(
        //   `Updated highlight.`,
        //   current,
        //   this.idMap.get(this.currentId)
        // );

        ToastService.showToast(
          this.$tc('labels.notes.updated'),
          ToastType.Success
        );
        CreateNote.closeSidePanelIfClickedFromPreview();
      } catch (err) {
        console.error('Error updating highlight', err);
        error = true;
      }
    } else {
      // We assume ranges and recreationData has been created somewhere else
      const newHighlight: NoteHighlight = {
        ...current.highlight,
        userComment: this.comment,
        hlcolor: this.selectedColor,
        id: null,
        createdTimestamp: null,
        timestampSortKey: null
      };

      try {
        const { highlight } = await this.addHighlightToPage({
          pid: this.pageId,
          site: this.site,
          highlight: newHighlight,
          page: this.page
        });
        this.idMap.set(this.currentId, {
          ...current,
          noteState: NoteState.REMOTE,
          highlight
        });
        this.noteMap.set(highlight.id, this.currentId);
        console.log(`Added highlight.`, highlight);
        ToastService.showToast(
          this.$tc('labels.notes.created'),
          ToastType.Success
        );
        NotesStore.updateTempMarkerToPermanent(highlight);
        NotesStore.userCanCreateHighlight = false;

        CreateNote.closeSidePanelIfClickedFromPreview();
      } catch (err) {
        console.log('Error adding highlight', err);
        error = true;
      }
    }
    if (!error) {
      // remove old color class and add the new.
      // if hlcolor is not set, we look for hl-default to remove
      const hlClassToRemove = current.highlight.hlcolor || 'hl-default';
      this.highlighter.removeClass(hlClassToRemove, this.currentId);
      this.highlighter.addClass(
        this.idMap.get(this.currentId).highlight.hlcolor,
        this.currentId
      );

      NotesStore.setNotesMode(NotesMode.VIEW);
      this.resetFields();
    }
  }

  cancelClicked() {
    if (this.isEditing) {
      // NotesClickedFrom 0 is from the panel and 1 is from the actual note on page.
      if (NotesStore.notesClickedFrom === 0) {
        NotesStore.setNotesMode(NotesMode.VIEW);
        return;
      } else if (NotesStore.notesClickedFrom === 1) {
        this.cancelHighlight();
      }
    }

    this.isSaving = false;
    this.cancelHighlight();
  }

  cancelHighlight(): void {
    if (!this.isEditing) {
      // Delete temp highlight and marker
      this.highlighter.remove(this.currentId);
      NotesStore.deleteTempHighlightMarker(this.currentId);
      NotesStore.userCanCreateHighlight = false;
    }
    this.setCurrentId(null);
    SidePanelStore.rightPanelIsOpen = false;
  }

  handleClose(): void {
    const userComment = this.currentNote
      ? this.currentNote.highlight.userComment
      : '';

    if (!userComment && this.comment === '') {
      this.cancelClicked();
    } else if (userComment !== this.comment) {
      this.showClosePrompt = true;
    } else {
      this.cancelClicked();
    }
  }

  closePrompt(): void {
    this.showClosePrompt = false;
  }

  private resetFields() {
    this.comment = '';
  }
}
